import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { CheckoutButton } from "@/components/CheckoutButton";
import { Reveal } from "@/components/Reveal";
import { COMMUNITY_NAME, PRECIO_MENSUAL } from "@/config";

const LINE_INITIAL = { y: "112%" };
const LINE_ANIMATE = { y: 0 };
const LINE_EASE = [0.22, 1, 0.36, 1];
const MSG_INITIAL = { opacity: 0, y: 14 };
const MSG_ANIMATE = { opacity: 1, y: 0 };

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.12em]">
    <motion.span
      className="block"
      initial={LINE_INITIAL}
      animate={LINE_ANIMATE}
      transition={{ duration: 0.9, delay, ease: LINE_EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const mensajes = [
  {
    autor: "Miembro",
    texto: "¿A alguien más le cuesta apagar la mente por la noche?",
    hora: "23:41",
  },
  {
    autor: "Miembro",
    texto: "Llevo todo el día pensando en esto y necesitaba compartirlo con alguien.",
    hora: "16:12",
  },
  {
    autor: "Tú",
    texto: "Por fin puedo explicar algo sin tener que simplificarlo.",
    hora: "16:15",
    propio: true,
  },
];

const ChatMockup = () => (
  <div className="relative mx-auto w-full max-w-sm">
    <div className="absolute -inset-3 rounded-[2.2rem] border border-[#8B7CF6]/15" />
    <div
      data-testid="whatsapp-mockup"
      className="relative overflow-hidden rounded-[2rem] border border-[#22304D] bg-[#0F1B2D] shadow-[0_16px_60px_rgba(0,0,0,0.45)]"
    >
      <div className="flex items-center gap-3 border-b border-[#22304D] bg-[#131D33] px-5 py-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#7C6CF0] to-[#6D8DFF] text-sm font-bold text-white">
          {COMMUNITY_NAME.charAt(0)}
        </div>
        <div>
          <p className="text-sm font-semibold text-[#F5F1EA]">{COMMUNITY_NAME}</p>
          <p className="flex items-center gap-1 text-[11px] text-[#9AA6BE]">
            <Lock className="h-3 w-3" strokeWidth={1.5} /> Comunidad privada
          </p>
        </div>
      </div>
      <div className="space-y-3 px-4 py-5">
        {mensajes.map((m, i) => (
          <motion.div
            key={m.texto}
            initial={MSG_INITIAL}
            animate={MSG_ANIMATE}
            transition={{ delay: 0.9 + i * 0.35, duration: 0.5 }}
            className={`flex ${m.propio ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                m.propio
                  ? "rounded-br-md bg-[#2E3F63]"
                  : "rounded-bl-md bg-[#1C2B45]"
              }`}
            >
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[#8B7CF6]">
                {m.autor}
              </p>
              <p className="mt-0.5 text-sm leading-snug text-[#E8E4DC]">{m.texto}</p>
              <p className="mt-1 text-right text-[10px] text-[#6B7690]">{m.hora}</p>
            </div>
          </motion.div>
        ))}
        <div className="flex items-center gap-2 rounded-full border border-[#22304D] bg-[#0B1120] px-4 py-2.5">
          <p className="text-xs text-[#6B7690]">Escribe lo que estás pensando…</p>
        </div>
      </div>
    </div>
    <p
      data-testid="mockup-disclaimer"
      className="mt-4 text-center text-[11px] text-[#6B7690]"
    >
      Conversaciones ilustrativas. No corresponden a miembros reales.
    </p>
  </div>
);

export const Hero = () => (
  <section
    id="inicio"
    className="relative overflow-hidden bg-[#0B1120] px-5 pb-24 pt-32 md:pt-40"
  >
    <div
      className="pointer-events-none absolute inset-x-0 -top-48 flex justify-center"
      aria-hidden="true"
    >
      <div className="h-[560px] w-[900px] max-w-none rounded-full bg-[#7C6CF0]/[0.09] blur-[130px]" />
    </div>

    <div className="relative mx-auto grid max-w-[1100px] grid-cols-1 items-center gap-14 lg:grid-cols-2">
      <div>
        <Reveal>
          <span
            data-testid="hero-eyebrow-tag"
            className="inline-block rounded-full border border-[#8B7CF6]/25 bg-[#8B7CF6]/8 px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8B7CF6] md:text-xs"
          >
            Coaching y comunidad privada para altas capacidades
          </span>
        </Reveal>

        <h1 className="mt-8 text-4xl font-semibold leading-[1.15] tracking-tight text-[#F5F1EA] sm:text-5xl lg:text-[3.4rem]">
          <MaskedLine delay={0.15}>Tu mente no tiene</MaskedLine>
          <MaskedLine delay={0.25}>
            que estar <span className="text-[#8B7CF6]">sola.</span>
          </MaskedLine>
        </h1>

        <Reveal delay={0.4}>
          <p className="mt-6 text-base leading-relaxed text-[#9AA6BE] md:text-lg">
            Coaching y comunidad privada de WhatsApp para personas con altas
            capacidades. Comparte lo que piensas, lo que sientes
            y las fricciones que vives cada día con personas que pueden
            entenderte de verdad.
          </p>
        </Reveal>

        <Reveal delay={0.5} className="mt-10">
          <CheckoutButton testId="cta-hero-join" origen="hero">
            Quiero entrar — {PRECIO_MENSUAL} €/mes
          </CheckoutButton>
          <p data-testid="hero-cta-subtext" className="mt-4 text-sm text-[#6B7690]">
            Acceso privado · Pago mensual · Cancela cuando quieras
          </p>
        </Reveal>
      </div>

      <Reveal delay={0.35}>
        <ChatMockup />
      </Reveal>
    </div>
  </section>
);
